/**
 * PropertyDesignFloors — AD-038 "Design & floors" block.
 *
 * ONE top-down surface replacing the old separate Floors-&-Grid + Design-Concepts
 * sections. UI contract: AVBP_PROPERTY_CONCEPT_VERSION_FLOOR_MOCKUP_2026-06-24.html.
 *
 *   ① One-design vs Multiple-options toggle
 *   ② Concept chips   (✎ rename / ✕ soft-delete / ＋ add · hidden when single-design)
 *   ③ Version chips   (✎ rename-stage / ✕ soft-delete / ＋ add · ACTIVE badge)
 *   ③ Floors list     (per active concept+version: upload/replace plan · Open grid · ✕)
 *      ＋ Add floor to this version
 *   ▸ Undo toast      (instant Undo on any soft-delete; then recoverable in Trash)
 *
 * Reuses the property grid/upload pipeline via the version-scoped store writers
 * (uploadVersionFloorPlan / saveVersionFloorBoundary / …) and the existing
 * FloorBoundaryTracer + CropRotateEditor + PdfPagePicker — no duplication of the
 * heavy boundary/grid machinery.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Btn3 } from './ui';
import {
  DESIGN_STAGES,
  liveConcepts,
  liveVersions,
  versionFloors,
  activeConcept as pickActiveConcept,
  activeVersion as pickActiveVersion,
  activeFloorPlan,
  buildGridId,
  makePropertyFloor,
  resolveFloorChoice,
  type DesignConcept,
  type DesignVersion,
  type LiveProperty,
  type PropertyFloor,
  type Pt2D,
  type GridSnapshot,
} from '../lib/propertyModel';
import {
  addDesignConcept,
  renamePropertyConcept,
  softDeletePropertyConcept,
  restorePropertyConcept,
  selectDesignConcept,
  addPropertyVersion,
  renamePropertyVersion,
  selectPropertyVersion,
  softDeletePropertyVersion,
  restorePropertyVersion,
  addFloorToPropertyVersion,
  softDeletePropertyVersionFloor,
  restorePropertyVersionFloor,
  uploadVersionFloorPlan,
  uploadVersionDerivedFloorPlan,
  saveVersionFloorBoundary,
  saveGridSnapshot,
} from '../lib/propertyStore';
import { savePropertyLink } from '../lib/intake';
import FloorNameCombobox from './FloorNameCombobox';
import { type Pt, computeGrid } from '../lib/vastuGrid';
import { cleanBoundary } from '../lib/gridOverlay';
import { bakeImage, derivedFileName, type EditRecipe, type EditableSource } from '../lib/imageCropRotate';
import { type FloorPlan, isPdfFloorPlanType } from '../lib/floorPlan';
import CropRotateEditor from './CropRotateEditor';
import FloorBoundaryTracer from './FloorBoundaryTracer';
import PdfPagePicker from './PdfPagePicker';

// ─── small style helpers (mirror the mockup) ────────────────────────────────

const chipBase: React.CSSProperties = {
  border: '1px solid var(--rule)', background: 'var(--surface)',
  borderRadius: 999, padding: '7px 12px', fontSize: 13, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 7,
};
const chipSel: React.CSSProperties = {
  background: 'var(--ok-wash, #e7f3ec)', borderColor: 'var(--ok, #bfe0cf)',
  color: '#15543c', fontWeight: 600,
};
const addChip: React.CSSProperties = {
  border: '1px dashed var(--rule)', background: 'var(--surface)',
  borderRadius: 999, padding: '7px 12px', fontSize: 12.5,
  color: 'var(--link)', cursor: 'pointer', fontWeight: 600,
};
const lvlLabel: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '0.06em', textTransform: 'uppercase',
  color: 'var(--ink-mute)', fontWeight: 700, margin: '16px 0 8px',
  display: 'flex', alignItems: 'center', gap: 8,
};
const crumbPill: React.CSSProperties = {
  background: 'var(--ok-wash, #eaf5ef)', color: 'var(--ok, #1f7a5a)',
  borderRadius: 999, padding: '1px 9px', fontWeight: 700, fontSize: 11,
  letterSpacing: 0, textTransform: 'none',
};
const badge: React.CSSProperties = {
  fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', borderRadius: 999,
  padding: '1px 7px', background: 'var(--warn-wash, #fbf3e1)', color: 'var(--warn, #9a6a12)',
};
const delBtn: React.CSSProperties = {
  width: 18, height: 18, lineHeight: '16px', textAlign: 'center', borderRadius: '50%',
  border: '1px solid var(--rule)', background: 'var(--surface)', color: 'var(--ink-mute)',
  fontSize: 12, cursor: 'pointer', padding: 0,
};
const hr: React.CSSProperties = { height: 1, background: 'var(--rule)', margin: '16px 0' };
const ctxNote: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)',
  padding: '9px 12px', fontSize: 12, color: 'var(--ink-mute)', margin: '10px 0 0',
};

function activePlanAsFloorPlan(floor: PropertyFloor): FloorPlan | null {
  const v = (floor.plans ?? []).find((p) => p.isActive);
  if (!v) return null;
  return { storagePath: v.storagePath, downloadURL: v.imageUrl, contentType: v.contentType, width: v.width, height: v.height };
}

// ─── Undo state ──────────────────────────────────────────────────────────────

interface UndoState { label: string; restore: () => Promise<void>; }

// ─── one floor row (mockup .floor) ───────────────────────────────────────────

function FloorRow({
  floor, propertyId, conceptId, versionId, northRotation, actor,
  clientId, projectId, tier, autoOpen, onMsg, onDelete,
}: {
  floor: PropertyFloor;
  propertyId: string;
  conceptId: string;
  versionId: string;
  northRotation: number | null;
  actor: { uid: string | null; name: string };
  clientId?: string;
  projectId?: string;
  tier?: string;
  /** Deep-link intent (?openGrid=1): open this row's tracer + scroll it into
   *  view, once, as soon as a plan is active. */
  autoOpen?: boolean;
  onMsg: (m: string) => void;
  onDelete: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [tracerOpen, setTracerOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const autoOpenedRef = useRef(false);
  const [editOpen, setEditOpen] = useState(false);
  const [baking, setBaking] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState<EditableSource | null>(null);
  const [pagePick, setPagePick] = useState<{ file: File; pdfBytes: ArrayBuffer; pageCount: number } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const activePlan = (floor.plans ?? []).find((v) => v.isActive);
  const floorPlan = activePlanAsFloorPlan(floor);
  // The tracer is CONTROLLED on `boundary` — it only draws what we hand back.
  // So the in-progress trace MUST live in local state and be fed straight in
  // (exactly what the working harness does). The old code gated this to null
  // until 3 saved points existed → the first two clicks never drew → you could
  // never trace. Seed from the saved boundary; re-seed only when the floor
  // changes (never mid-trace). Persisting happens on COMMIT (handleBoundaryChange).
  const persistedBoundary = (floor.boundary && floor.boundary.length ? (floor.boundary as Pt[]) : null);
  const [localBoundary, setLocalBoundary] = useState<Pt[] | null>(persistedBoundary);
  useEffect(() => { setLocalBoundary(persistedBoundary); }, [floor.floorId]); // eslint-disable-line react-hooks/exhaustive-deps
  const north = floor.northRotation ?? northRotation ?? 0;
  const hasGrid = Boolean(floor.boundary && floor.boundary.length >= 3);
  const hasActivePlan = Boolean(activePlan);

  // Deep-link auto-open: when staff arrive via "Open grid on Property page →"
  // (?openGrid=1) and this is the chosen gridable floor, open the tracer and
  // scroll it into view ONCE — as soon as a plan is active (handles the brief
  // post-load gap where activePlan hasn't streamed back yet). The ref guard
  // means it never re-opens after staff close it.
  useEffect(() => {
    if (autoOpen && hasActivePlan && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setTracerOpen(true);
      requestAnimationFrame(() => {
        rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [autoOpen, hasActivePlan]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (isPdfFloorPlanType(file.type)) {
      const pdfBytes = await file.arrayBuffer();
      const { getPdfPageCount } = await import('../lib/imageCropRotate');
      const pageCount = await getPdfPageCount(pdfBytes);
      if (pageCount > 1) { setPagePick({ file, pdfBytes, pageCount }); return; }
    }
    await doUpload(file);
  }

  async function doUpload(file: File) {
    setUploading(true);
    try {
      await uploadVersionFloorPlan(propertyId, conceptId, versionId, floor.floorId, file, actor.uid ?? 'unknown');
      onMsg(`Floor plan uploaded for ${floor.label}.`);
    } catch (err) {
      onMsg(`Upload failed: ${(err as Error).message}`);
    } finally { setUploading(false); }
  }

  async function openEditor() {
    if (!activePlan) return;
    try {
      const { loadEditableSource } = await import('../lib/imageCropRotate');
      const src = await loadEditableSource(activePlan.imageUrl, activePlan.contentType);
      setResolvedSrc(src); setEditOpen(true);
    } catch (err) { onMsg(`Could not open editor: ${(err as Error).message}`); }
  }

  async function handleApplyEdit(recipe: EditRecipe) {
    if (!activePlan || !resolvedSrc) return;
    setBaking(true);
    try {
      const name = derivedFileName(activePlan.storagePath.split('/').pop() ?? 'plan', resolvedSrc.bakeMime);
      const baked = await bakeImage(resolvedSrc.image, resolvedSrc.width, resolvedSrc.height, recipe, name, resolvedSrc.bakeMime);
      await uploadVersionDerivedFloorPlan(propertyId, conceptId, versionId, floor.floorId, baked.file, baked.width, baked.height);
      setEditOpen(false); setResolvedSrc(null);
      onMsg(`Floor plan edited for ${floor.label}.`);
    } catch (err) { onMsg(`Could not apply edit: ${(err as Error).message}`); }
    finally { setBaking(false); }
  }

  async function handleBoundaryChange(pts: Pt[], commit?: boolean) {
    // 1) reflect the click IMMEDIATELY in the drawing — verbatim, no clean (the
    //    tracer cleans on close). This is what was missing: a controlled tracer
    //    that never saw its own points, so nothing ever drew.
    setLocalBoundary(pts.length ? pts : null);
    // 2) persist + compute the grid ONLY on a real commit (boundary closed),
    //    honouring the tracer's contract. In-progress clicks stay local + instant.
    if (!commit) return;
    const cleaned = cleanBoundary(pts);
    let gridId: string | undefined;
    // GRID_ENGINE_SEAM (AD-038: gridId keyed by concept+version+floor).
    // Gap #7 fix: grid-making is INTRINSIC to the property (boundary → grid) and
    // needs ONLY the propertyId — it must NOT be gated behind project/intake
    // context. Previously the whole snapshot block required `clientId && tier`, so
    // a boundary closed on the Property page before it was linked to a valid-tier
    // project (the audit's independent "grid-making" step) persisted a boundary
    // with gridId:null — no `properties/{pid}/grids/{gridId}` doc, no
    // `floors[i].gridId` FK — which then left every downstream mark's
    // `markingGridId` null. Create the snapshot whenever a closeable boundary
    // exists; only the intake-FK back-write still needs the project context.
    if (cleaned.length >= 3) {
      try {
        const ap = activeFloorPlan(floor);
        const gridResult = computeGrid({ boundary: cleaned as Pt[], rotation: north });
        const versionNumber = Date.now();
        const planVersionId = ap?.planId ?? 'no-plan';
        gridId = buildGridId(floor.floorId, planVersionId, versionNumber, conceptId, versionId);
        const g = gridResult as unknown as Record<string, unknown>;
        const snapshot: GridSnapshot = {
          gridId, floorId: floor.floorId, planVersionId, version: versionNumber,
          northRotation: north, boundary: cleaned as Pt2D[], engineJson: g,
          engineVersion: typeof g.engine_version === 'string' ? g.engine_version : undefined,
          boundaryHash: g.meta ? ((g.meta as Record<string, unknown>).inputBoundaryHash as string | undefined) : undefined,
          computedAt: new Date().toISOString(), computedBy: actor.uid ?? undefined, isActive: true,
        };
        await saveGridSnapshot(propertyId, snapshot);
        // Intake FK back-write — only meaningful inside a project intake context.
        if (clientId && tier) {
          await savePropertyLink({ clientId, projectId }, tier, propertyId, gridId, actor.uid ?? 'system');
        }
      } catch (err) {
        gridId = undefined;
        onMsg(`Could not save grid: ${(err as Error).message}`);
      }
    }
    try {
      await saveVersionFloorBoundary(propertyId, conceptId, versionId, floor.floorId, cleaned as Pt2D[], pts as Pt2D[], gridId);
      onMsg(`Boundary saved for ${floor.label}.`);
    } catch (err) { onMsg(`Could not save boundary: ${(err as Error).message}`); }
  }

  return (
    <div ref={rowRef} style={{ border: autoOpen && tracerOpen ? '1.5px solid var(--accent, #2f5d8a)' : '1px solid var(--rule)', borderRadius: 'var(--r-sm)', padding: '11px 13px', margin: '8px 0', background: 'var(--surface)', scrollMarginTop: 80 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, background: 'var(--bg)', border: '1px solid var(--rule)', borderRadius: 6, padding: '3px 8px' }}>
          {floor.floorId}
        </span>
        <span style={{ fontWeight: 600 }}>{floor.label}</span>
        <span style={{ flex: 1 }} />
        {activePlan ? (
          <span style={{ fontSize: 11, color: 'var(--ok, #1f7a5a)', background: 'var(--ok-wash, #eaf5ef)', borderRadius: 999, padding: '2px 9px' }}>plan ✓</span>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--ink-mute)', background: 'var(--bg)', border: '1px solid var(--rule)', borderRadius: 999, padding: '2px 9px' }}>no plan yet</span>
        )}
        <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleFileChange} />
        <Btn3 intent="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? 'Uploading…' : activePlan ? 'Replace plan' : 'Upload plan'}
        </Btn3>
        <Btn3 intent={hasGrid ? 'grn' : 'ghost'} size="sm" onClick={() => setTracerOpen((s) => !s)} disabled={!activePlan} title={!activePlan ? 'Upload a plan first' : undefined}>
          {tracerOpen ? 'Close grid' : 'Open grid'}
        </Btn3>
        <button style={delBtn} title="Delete floor" onClick={onDelete} aria-label={`Delete ${floor.label}`}>✕</button>
      </div>

      {activePlan && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Btn3 intent="ghost" size="sm" onClick={openEditor} disabled={baking}>
            {baking ? 'Applying…' : 'Crop & rotate'}
          </Btn3>
        </div>
      )}

      {tracerOpen && activePlan && (
        <div style={{ border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginTop: 10 }}>
          <FloorBoundaryTracer
            floorplan={floorPlan}
            boundary={localBoundary}
            north={north}
            onUpload={(file) => { void doUpload(file); }}
            onPickProjectFile={() => {}}
            onClearPlan={() => {}}
            onEditPlan={openEditor}
            onChangeBoundary={handleBoundaryChange}
            uploading={uploading}
            projectImageFiles={[]}
          />
        </div>
      )}

      {pagePick && (
        <PdfPagePicker
          fileName={pagePick.file.name}
          pdfBytes={pagePick.pdfBytes}
          pageCount={pagePick.pageCount}
          onConfirm={async (_page) => { const f = pagePick.file; setPagePick(null); await doUpload(f); }}
          onCancel={() => setPagePick(null)}
        />
      )}

      {editOpen && resolvedSrc && activePlan && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--r)', maxWidth: 800, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>Crop & rotate — {floor.label}</span>
              <Btn3 intent="ghost" size="sm" onClick={() => { setEditOpen(false); setResolvedSrc(null); }}>Cancel</Btn3>
            </div>
            <CropRotateEditor
              imageUrl={resolvedSrc.previewUrl}
              srcWidth={resolvedSrc.width}
              srcHeight={resolvedSrc.height}
              onApply={handleApplyEdit}
              onCancel={() => { setEditOpen(false); setResolvedSrc(null); }}
              busy={baking}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── editable chip (rename inline) ───────────────────────────────────────────

function EditableChip({
  selected, label, sub, badgeText, canDelete, onPick, onRename, onDelete,
}: {
  selected: boolean;
  label: string;
  sub?: string;
  badgeText?: string;
  canDelete: boolean;
  onPick: () => void;
  onRename: (next: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  function commit() { setEditing(false); const t = draft.trim(); if (t && t !== label) onRename(t); else setDraft(label); }
  return (
    <span style={{ ...chipBase, ...(selected ? chipSel : {}) }} onClick={editing ? undefined : onPick}>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } if (e.key === 'Escape') { setDraft(label); setEditing(false); } }}
          onClick={(e) => e.stopPropagation()}
          style={{ border: '1px solid var(--link)', borderRadius: 4, padding: '0 4px', minWidth: 60, font: 'inherit' }}
        />
      ) : (
        <span>{label}{sub ? <span style={{ fontSize: 11, color: 'var(--ink-mute)', marginLeft: 5 }}>{sub}</span> : null}</span>
      )}
      {badgeText && <span style={badge}>{badgeText}</span>}
      <button style={delBtn} title="Rename" onClick={(e) => { e.stopPropagation(); setDraft(label); setEditing(true); }}>✎</button>
      {canDelete && <button style={delBtn} title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(); }}>✕</button>}
    </span>
  );
}

// ─── main block ──────────────────────────────────────────────────────────────

export default function PropertyDesignFloors({
  prop, actor, clientId, projectId, tier, autoOpenGrid,
}: {
  prop: LiveProperty;
  actor: { uid: string | null; name: string };
  clientId?: string;
  projectId?: string;
  tier?: string;
  /** Arrived via the L1A rollup deep-link (?openGrid=1): scroll to a gridable
   *  floor and auto-open its tracer, or explain what's missing if not gridable. */
  autoOpenGrid?: boolean;
}) {
  const propertyId = prop.propertyId;
  const blockRef = useRef<HTMLDivElement | null>(null);
  const concepts = useMemo(() => liveConcepts(prop.concepts), [prop.concepts]);
  const [msg, setMsg] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoState | null>(null);
  const [busy, setBusy] = useState(false);

  // "Multiple options" is ON when more than one live concept exists; staff can
  // also turn it on explicitly to split a single design into Concept A / B.
  const [multiOpen, setMultiOpen] = useState(concepts.length > 1);
  const isMulti = multiOpen || concepts.length > 1;

  // Selected concept + version drive the floors list. Derive from the doc
  // (active = selected/first live), keep a UI override per render.
  const [pickedConceptId, setPickedConceptId] = useState<string | null>(null);
  const [pickedVersionId, setPickedVersionId] = useState<string | null>(null);

  const curConcept: DesignConcept | null =
    concepts.find((c) => c.conceptId === pickedConceptId) ?? pickActiveConcept(prop) ?? null;
  const curVersions = useMemo(() => liveVersions(curConcept), [curConcept]);
  const curVersion: DesignVersion | null =
    curVersions.find((v) => v.versionId === pickedVersionId) ?? (curConcept ? pickActiveVersion(curConcept) : null);
  const floors = useMemo(() => versionFloors(curVersion), [curVersion]);

  // ── Deep-link (?openGrid=1) resolution ──────────────────────────────────────
  // Pick the floor to auto-open: prefer one already gridded (boundary present)
  // so reopening shows the existing grid; else the first floor with an active
  // plan. If nothing is gridable, surface a clear hint instead of a dead button.
  const gridableFloors = useMemo(
    () => floors.filter((f) => (f.plans ?? []).some((p) => p.isActive)),
    [floors],
  );
  const autoOpenFloorId = useMemo(() => {
    if (!autoOpenGrid) return null;
    const gridded = gridableFloors.find((f) => f.boundary && f.boundary.length >= 3);
    return (gridded ?? gridableFloors[0])?.floorId ?? null;
  }, [autoOpenGrid, gridableFloors]);
  const openGridNoFloors = Boolean(autoOpenGrid) && floors.length === 0;
  const openGridNoPlan = Boolean(autoOpenGrid) && floors.length > 0 && gridableFloors.length === 0;

  // When the deep-link can't auto-open a tracer (no plan / no floors yet), bring
  // the Design & floors block into view so staff land on the action, not mid-page.
  // (When a floor IS gridable, the FloorRow scrolls itself into view instead.)
  const deepLinkScrolledRef = useRef(false);
  useEffect(() => {
    if ((openGridNoFloors || openGridNoPlan) && !deepLinkScrolledRef.current) {
      deepLinkScrolledRef.current = true;
      requestAnimationFrame(() => blockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [openGridNoFloors, openGridNoPlan]);

  function onMsg(m: string) { setMsg(m); setTimeout(() => setMsg(null), 4000); }
  function showUndo(label: string, restore: () => Promise<void>) {
    setUndo({ label, restore });
    setTimeout(() => setUndo((u) => (u && u.label === label ? null : u)), 9000);
  }
  async function runUndo() {
    if (!undo) return;
    const r = undo.restore; setUndo(null);
    try { await r(); onMsg('Restored.'); } catch (e) { onMsg(`Undo failed: ${(e as Error).message}`); }
  }

  // ── ① one design vs multiple ──
  async function ensureMultiple() {
    setMultiOpen(true);
    // If there is no explicit concept yet (single implicit Default), seed Concept A
    // so staff have something to add Concept B alongside.
    if (concepts.length === 0) {
      setBusy(true);
      try { await addDesignConcept(propertyId, 'Concept A'); }
      finally { setBusy(false); }
    }
  }

  // ── ① concept actions ──
  async function addConceptAction() {
    setBusy(true);
    try {
      const name = `Concept ${String.fromCharCode(65 + concepts.length)}`;
      const id = await addDesignConcept(propertyId, name);
      if (id) { setPickedConceptId(id); setPickedVersionId(null); }
    } finally { setBusy(false); }
  }
  // ── Part 3 (AD-040) — "Add architect's drawing set" directly from the
  // Property page: creates a NEW Concept (stage + label), selects it, and opens
  // the floors list so staff add floors + upload plans via the existing
  // version-scoped pipeline — no intake required. ──
  const [addingSet, setAddingSet] = useState(false);
  const [setStage, setSetStage] = useState<string>('Concept');
  const [setLabel, setSetLabel] = useState('');
  async function addDrawingSetAction() {
    setBusy(true);
    try {
      const name = setLabel.trim() || `Drawing set ${String.fromCharCode(65 + concepts.length)}`;
      const id = await addDesignConcept(propertyId, name);
      if (id) {
        // A new concept is born with a seed v1 version (stage "Concept"); set the
        // chosen stage on it (DEFAULT_VERSION_ID = 'v1') so the timeline reads right.
        if (setStage && setStage !== 'Concept') {
          await renamePropertyVersion(propertyId, id, 'v1', setStage).catch(() => {});
        }
        if (concepts.length > 0) setMultiOpen(true); // surface the concept layer
        setPickedConceptId(id);
        setPickedVersionId(null);
        setAddingSet(false);
        setSetLabel('');
        setSetStage('Concept');
        onMsg('Drawing set added — add the floors and upload each plan below.');
      }
    } finally { setBusy(false); }
  }

  function pickConcept(conceptId: string) { setPickedConceptId(conceptId); setPickedVersionId(null); selectDesignConcept(propertyId, conceptId).catch(() => {}); }
  function renameConceptAction(conceptId: string, name: string) { renamePropertyConcept(propertyId, conceptId, name).catch(() => onMsg('Rename failed.')); }
  async function deleteConceptAction(conceptId: string, name: string) {
    setBusy(true);
    try {
      await softDeletePropertyConcept(propertyId, conceptId);
      onMsg(`Deleted "${name}".`);
      showUndo(name, () => restorePropertyConcept(propertyId, conceptId));
      if (pickedConceptId === conceptId) { setPickedConceptId(null); setPickedVersionId(null); }
    } finally { setBusy(false); }
  }

  // ── ② version actions ──
  async function addVersionAction() {
    if (!curConcept) return;
    setBusy(true);
    try {
      await addPropertyVersion(propertyId, curConcept.conceptId, 'Developed', { carryFloors: true });
      setPickedVersionId(null); // let the new active version surface
      onMsg('Version added — previous floors carried forward.');
    } finally { setBusy(false); }
  }
  function pickVersion(versionId: string) { if (!curConcept) return; setPickedVersionId(versionId); selectPropertyVersion(propertyId, curConcept.conceptId, versionId).catch(() => {}); }
  function renameVersionAction(versionId: string, stage: string) { if (!curConcept) return; renamePropertyVersion(propertyId, curConcept.conceptId, versionId, stage).catch(() => onMsg('Rename failed.')); }
  async function deleteVersionAction(versionId: string, stage: string) {
    if (!curConcept) return;
    setBusy(true);
    try {
      await softDeletePropertyVersion(propertyId, curConcept.conceptId, versionId);
      onMsg(`Deleted version · ${stage}.`);
      const cid = curConcept.conceptId;
      showUndo(`Version · ${stage}`, () => restorePropertyVersion(propertyId, cid, versionId));
      if (pickedVersionId === versionId) setPickedVersionId(null);
    } finally { setBusy(false); }
  }

  // ── ③ floor actions ──
  const [addingFloor, setAddingFloor] = useState(false);
  const [floorChoice, setFloorChoice] = useState('');

  async function addFloorAction(typed?: string) {
    const name = (typed ?? floorChoice).trim();
    if (!curConcept || !curVersion || !name) return;
    // resolve the TYPED label → a registry { floorId slot, label } — a custom
    // name ("Site / plot map") takes a free slot + keeps its label; presets map
    // to their canonical FloorId. The grid reads the boundary, not the floorId.
    const { floorId, label } = resolveFloorChoice(name, floors.map((f) => f.floorId));
    setBusy(true);
    try {
      await addFloorToPropertyVersion(propertyId, curConcept.conceptId, curVersion.versionId, makePropertyFloor(floorId, label));
      setFloorChoice(''); setAddingFloor(false);
      onMsg(`Floor added — ${label}.`);
    } finally { setBusy(false); }
  }
  async function deleteFloorAction(floorId: string, label: string) {
    if (!curConcept || !curVersion) return;
    setBusy(true);
    const cid = curConcept.conceptId, vid = curVersion.versionId;
    try {
      await softDeletePropertyVersionFloor(propertyId, cid, vid, floorId);
      onMsg(`Deleted floor ${floorId}.`);
      showUndo(`Floor ${floorId} · ${label}`, () => restorePropertyVersionFloor(propertyId, cid, vid, floorId));
    } finally { setBusy(false); }
  }

  const conceptCrumb = isMulti && curConcept ? curConcept.name : 'Default design';
  const versionCrumb = curVersion ? `${curVersion.stage}` : '—';
  const floorCtx = `${conceptCrumb} · ${versionCrumb}`;

  return (
    <div ref={blockRef} style={{ scrollMarginTop: 80 }}>
    <Box title="Design &amp; floors" note="Concept ▸ Version ▸ Floors">
      {/* Deep-link banner (?openGrid=1) — staff came here to grid this property
          but it isn't gridable yet. Tell them exactly what to do, don't leave a
          dead "Open grid" button. */}
      {(openGridNoFloors || openGridNoPlan) && (
        <div
          style={{
            background: 'var(--warn-wash, #fbf3e1)', border: '1px solid var(--warn, #e8d29a)',
            borderRadius: 'var(--r-sm)', padding: '10px 13px', marginBottom: 12,
            fontSize: 12.5, color: 'var(--warn-deep, #7a5410)', lineHeight: 1.55,
          }}
        >
          <strong>To grid this property:</strong>{' '}
          {openGridNoFloors
            ? 'add a floor (or “Site / plot map”) below, upload its plan, then “Open grid”.'
            : 'upload a plan on the floor below — “Open grid” turns on the moment a plan is attached.'}
        </div>
      )}
      {msg && (
        <div style={{ padding: '6px 10px', background: 'var(--bg)', borderRadius: 'var(--r-sm)', fontSize: 12.5, marginBottom: 10, border: '1px solid var(--rule)' }}>{msg}</div>
      )}

      {/* Part 3 (AD-040) — Add an architect's drawing set directly from the
          property (no intake). Creates a NEW concept (stage + label); floors +
          plan uploads use the existing per-floor version-scoped pipeline below. */}
      <div style={{ border: '1.5px dashed var(--accent-line, #d3e0ec)', background: 'var(--accent-wash, #eef3f8)', borderRadius: 'var(--r-sm)', padding: 14, margin: '0 0 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-deep, #274b63)' }}>
            <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', borderRadius: 999, padding: '1px 7px', background: '#e9f7ef', color: '#0f7a4d', marginRight: 8 }}>NEW</span>
            Add an architect's drawing set to this property
          </div>
          {!addingSet && (
            <Btn3 intent="ghost" size="sm" onClick={() => setAddingSet(true)} disabled={busy}>＋ Add drawing set</Btn3>
          )}
        </div>
        <p style={{ fontSize: 12, color: 'var(--accent-deep, #274b63)', margin: '8px 0 0' }}>
          Adds a new <b>design concept</b> (stage + label) — the same fields as the L1C intake card, but you don't need an intake. Then add each floor &amp; upload its DWG/PDF/image below, and trace the grid as usual.
        </p>
        {addingSet && (
          <div style={{ background: '#fff', border: '1px solid var(--accent-line, #d3e0ec)', borderRadius: 'var(--r-sm)', padding: 12, marginTop: 11 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>Drawing stage</label>
                <select value={setStage} onChange={(e) => setSetStage(e.target.value)} style={{ width: '100%', border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', padding: '8px 10px', fontSize: 13 }}>
                  {DESIGN_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>Label / note (optional)</label>
                <input value={setLabel} onChange={(e) => setSetLabel(e.target.value)} placeholder="e.g. Concept v2 · after NE correction" style={{ width: '100%', border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', padding: '8px 10px', fontSize: 13 }} />
              </div>
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <Btn3 intent="grn" size="sm" onClick={addDrawingSetAction} disabled={busy}>Add to property</Btn3>
              <Btn3 intent="ghost" size="sm" onClick={() => { setAddingSet(false); setSetLabel(''); setSetStage('Concept'); }}>Cancel</Btn3>
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '8px 0 0' }}>
              After it's created, the new set is selected below — add its floors and upload each plan with the per-floor controls.
            </p>
          </div>
        )}
      </div>

      {/* ① one design or many */}
      <p style={{ fontSize: 13, margin: '0 0 10px' }}>Does this building have one design, or multiple options to compare?</p>
      <div style={{ display: 'inline-flex', border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
        <button
          onClick={() => setMultiOpen(false)}
          disabled={concepts.length > 1}
          style={{ border: 'none', padding: '7px 14px', fontSize: 12.5, cursor: concepts.length > 1 ? 'not-allowed' : 'pointer', background: !isMulti ? 'var(--ink)' : 'var(--surface)', color: !isMulti ? '#fff' : 'var(--ink-mute)', fontWeight: !isMulti ? 600 : 400 }}
        >
          One design
        </button>
        <button
          onClick={ensureMultiple}
          style={{ border: 'none', padding: '7px 14px', fontSize: 12.5, cursor: 'pointer', background: isMulti ? 'var(--ink)' : 'var(--surface)', color: isMulti ? '#fff' : 'var(--ink-mute)', fontWeight: isMulti ? 600 : 400 }}
        >
          Multiple options (concepts)
        </button>
      </div>
      {!isMulti && (
        <p style={ctxNote}>With one design, the concept layer stays hidden — you go straight to versions &amp; floors. Choose “Multiple options” only when an architect gives you 2+ schemes to compare.</p>
      )}

      {/* ① CONCEPTS (only when multiple) */}
      {isMulti && (
        <>
          <div style={hr} />
          <div style={lvlLabel}>① Design concept</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {concepts.map((c) => (
              <EditableChip
                key={c.conceptId}
                selected={c.conceptId === curConcept?.conceptId}
                label={c.name}
                badgeText={c.conceptId === curConcept?.conceptId ? 'SELECTED' : undefined}
                canDelete={concepts.length > 1}
                onPick={() => pickConcept(c.conceptId)}
                onRename={(n) => renameConceptAction(c.conceptId, n)}
                onDelete={() => deleteConceptAction(c.conceptId, c.name)}
              />
            ))}
            <button style={addChip} onClick={addConceptAction} disabled={busy}>＋ Add concept</button>
          </div>
          <p style={ctxNote}><b>Single-click</b> a concept to work in it · <b>✎</b> to rename · <b>✕</b> to delete it (with its versions &amp; floors). “＋ Add concept” creates a new one.</p>
        </>
      )}

      {/* Provenance — concept written through from an intake (AD-040). */}
      {curConcept?.source?.kind === 'intake' && (
        <div style={{ fontSize: 11.5, color: 'var(--accent-deep, #274b63)', background: 'var(--accent-wash, #eef3f8)', border: '1px solid var(--accent-line, #d3e0ec)', borderRadius: 'var(--r-sm)', padding: '8px 12px', margin: '10px 0 0' }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', borderRadius: 999, padding: '1px 7px', background: '#e9f7ef', color: '#0f7a4d', marginRight: 6 }}>FROM INTAKE</span>
          This concept arrived from {curConcept.source.tier ? `an ${curConcept.source.tier} intake` : 'an intake'}
          {curConcept.source.sourceIntakeId ? ` · ${curConcept.source.sourceIntakeId}` : ''}
          {curConcept.source.at ? ` · ${curConcept.source.at.slice(0, 10)}` : ''}. Edits here update the property record; the report engine reads floors &amp; grids from {propertyId}.
        </div>
      )}

      {/* ② VERSIONS */}
      <div style={hr} />
      <div style={lvlLabel}>② Version <span style={crumbPill}>{conceptCrumb}</span></div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {curVersions.map((v) => (
          <EditableChip
            key={v.versionId}
            selected={v.versionId === curVersion?.versionId}
            label={v.versionId}
            sub={v.stage}
            badgeText={v.isActive ? 'ACTIVE' : undefined}
            canDelete={curVersions.length > 1}
            onPick={() => pickVersion(v.versionId)}
            onRename={(stage) => renameVersionAction(v.versionId, stage)}
            onDelete={() => deleteVersionAction(v.versionId, v.stage)}
          />
        ))}
        <button style={addChip} onClick={addVersionAction} disabled={busy || !curConcept}>＋ Add version</button>
      </div>
      <p style={ctxNote}>
        Versions are created here as proper items (with a stage — {DESIGN_STAGES.join(' / ')}), <b>not typed on each floor</b>. “＋ Add version” carries the previous floors forward so you replace only what changed.
      </p>

      {/* ③ FLOORS */}
      <div style={hr} />
      <div style={lvlLabel}>③ Floors <span style={crumbPill}>{floorCtx}</span></div>
      {floors.length === 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)', padding: '6px 2px' }}>No floors in this version yet. Add the first floor below.</p>
      ) : (
        floors.map((f) => (
          <FloorRow
            key={f.floorId}
            floor={f}
            propertyId={propertyId}
            conceptId={curConcept?.conceptId ?? ''}
            versionId={curVersion?.versionId ?? ''}
            northRotation={prop.northRotation ?? null}
            actor={actor}
            clientId={clientId}
            projectId={projectId}
            tier={tier}
            autoOpen={f.floorId === autoOpenFloorId}
            onMsg={onMsg}
            onDelete={() => deleteFloorAction(f.floorId, f.label)}
          />
        ))
      )}

      {addingFloor ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 6 }}>
          <div style={{ flex: 1 }}>
            {/* Creatable combobox: type a floor / surface name (incl. "Site / plot
                map") or pick a preset; free values are added (owner directive). */}
            <FloorNameCombobox
              value={floorChoice}
              onChange={setFloorChoice}
              onCommit={(label) => { void addFloorAction(label); }}
              usedLabels={floors.map((f) => f.label)}
              placeholder="e.g. Site / plot map, Ground Floor…"
              ariaLabel="Floor or surface name"
            />
          </div>
          <Btn3 intent="grn" size="sm" onClick={() => { void addFloorAction(); }} disabled={busy || !floorChoice.trim()}>Add</Btn3>
          <Btn3 intent="ghost" size="sm" onClick={() => { setAddingFloor(false); setFloorChoice(''); }}>Cancel</Btn3>
        </div>
      ) : (
        <button style={{ ...addChip, display: 'block', width: '100%', borderRadius: 'var(--r-sm)', padding: 10, marginTop: 6, textAlign: 'left' }} onClick={() => setAddingFloor(true)} disabled={!curVersion}>
          ＋ Add floor to this version
        </button>
      )}
      <p style={ctxNote}>A floor you add here automatically belongs to <b>{floorCtx}</b>. Each floor carries its own plan &amp; grid for this version. Deletes are safe — an instant Undo appears, and the item stays recoverable in Trash for 30 days.</p>

      {/* ▸ Undo toast (mockup .toast) */}
      {undo && (
        <div style={{ position: 'fixed', left: '50%', bottom: 26, transform: 'translateX(-50%)', background: '#0e1217', color: '#eaf0f4', borderRadius: 10, padding: '11px 16px', fontSize: 13, display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 6px 24px rgba(0,0,0,0.25)', zIndex: 950 }}>
          <span>Deleted “{undo.label}” — moved to Trash.</span>
          <button onClick={runUndo} style={{ background: 'transparent', border: '1px solid #3a4654', color: '#9fd4bd', borderRadius: 7, padding: '5px 12px', fontSize: 12.5, cursor: 'pointer', fontWeight: 600 }}>Undo</button>
          <button onClick={() => setUndo(null)} style={{ background: 'transparent', border: 'none', color: '#6b7682', cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </Box>
    </div>
  );
}
